
import Vue from 'vue';
import type { PropOptions } from 'vue';
import { validationMixin } from 'vuelidate';
import { requiredIf, requiredUnless } from 'vuelidate/lib/validators';
import type { ValidationRule } from 'vuelidate/lib/validators';
import JobsCompanySeriesStagesQuery from '@/apollo/queries/Jobs/JobsCompanySeriesStages.query';
import BravadoTextInputV3 from '@/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import BravadoToggle from '@/components/common/BravadoToggle/BravadoToggle.vue';
import { jobsInquiryFormFields } from '@/components/partials/jobs/constants/inquiryFields';
import { jobsInquiryRoleNames } from '@/components/partials/jobs/constants/optionsDicts';
import {
  companyStages,
  employmentTypes,
  jobsDealBrakers,
  salesRoleNames,
} from '@/components/partials/jobs/constants/selectableOptions';
import { metroAreas } from '@/components/partials/jobs/JobsBasicInformation/JobsBasicInformationCity/constants';
import { formFields } from '@/components/partials/jobs/JobsDesiredCriteriaModal/constants';
import JobsDesiredCriteriaModalFormCityAutocomplete from '@/components/partials/jobs/JobsDesiredCriteriaModal/JobsDesiredCriteriaModalFormCityAutocomplete.vue';
import PortfolioAutofocusFormFieldMixin from '@/components/partials/portfolio/PortfolioAutofocusFormFieldMixin';
import { omit } from '@/components/utils/core/object';
import type {
  City,
  CompanySeriesStage,
  CurrentUser,
  JobsInquiry as JobsInquiryType,
  CompanySeriesStageSlug,
} from '@/types/graphql';
import { JobsInquiryDealBreaker, JobsInquiryPosition } from '@/types/graphql';
import type {
  Form,
  InquiryInput,
} from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySectionProfile/types';
import JobsDropdown from '~/components/partials/jobs/JobsDropdown/JobsDropdown.vue';

interface IDropdownOption {
  title: string;
  key: string | number;
}

interface IData {
  form: Omit<Partial<Form>, 'desiredCompanySeriesStages'> & {
    cityId?: string | null;
    desiredCompanySeriesStages?: CompanySeriesStageSlug[];
  };
  jobsInquiryFormFields: Record<string, string>;
  locationDropdownOptions: IDropdownOption[];
  companyStageDropdownOptions: IDropdownOption[];
  employmentTypeDropdownOptions: IDropdownOption[];
  isCustomLocationInputVisible: boolean;
  attemptedSubmission: boolean;
  loading: boolean;
  jobsCompanySeriesStages?: CompanySeriesStage[];
  isCustomPositionInputVisible: boolean;
  formFields: Record<string, string>;
}

const NO_SDR_KEY = 'without_sdr';

export default Vue.extend({
  name: 'JobsDesiredCriteriaModalForm',
  components: {
    BravadoToggle,
    BravadoTextInputV3,
    JobsDropdown,
    JobsDesiredCriteriaModalFormCityAutocomplete,
  },
  apollo: {
    jobsCompanySeriesStages: {
      query: JobsCompanySeriesStagesQuery,
      error(e): void {
        this.$notice.error(e);
      },
    },
  },
  mixins: [validationMixin, PortfolioAutofocusFormFieldMixin],
  // FIXME
  validations(): { form: Record<string, Record<string, ValidationRule>> } {
    return {
      form: {
        cityId: {
          required: requiredUnless(() => {
            return this.adminPrivilege;
          }),
        },
        [jobsInquiryFormFields.POSITION_MAIN]: {
          required: requiredIf(() => {
            if (this.adminPrivilege) return false;
            if (this.form.positionMain) return true;

            return !this.form.positionOther;
          }),
        },
        [jobsInquiryFormFields.DEAL_BREAKERS_MAIN]: {
          required: requiredIf(() => {
            if (this.adminPrivilege) return false;
            if (this.form.dealBreakersMain?.length) return true;

            return !this.form.dealBreakerOther;
          }),
        },
        [jobsInquiryFormFields.POSITION_OTHER]: {
          required: requiredIf(() => {
            return !this.form.positionMain;
          }),
        },
        [jobsInquiryFormFields.EMPLOYMENT_TYPES]: {
          required: requiredUnless(() => {
            return this.adminPrivilege;
          }),
        },
        desiredCompanySeriesStages: {
          required: requiredUnless(() => {
            return this.adminPrivilege;
          }),
        },
      },
    };
  },
  props: {
    inquiry: {
      type: Object,
      required: true,
    } as PropOptions<JobsInquiryType>,
    profile: {
      type: Object,
      required: true,
    } as PropOptions<CurrentUser>,
    adminPrivilege: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
  },
  data(): IData {
    const locationDropdownOptions: IDropdownOption[] = metroAreas.map(city => {
      return {
        title: city.title,
        key: city.id,
      };
    });

    const cityId = this.profile?.city?.id;

    return {
      form: {
        cityId,
        positionMain: this.inquiry.positionMain,
        positionOther: this.inquiry.positionOther,
        willingToRelocate: this.inquiry.willingToRelocate || false,
        desiredCompanySeriesStages: this.inquiry.desiredCompanySeriesStages?.map(({ slug }) => slug),
        employmentTypes: this.inquiry.employmentTypes,
        dealBreakersMain: this.inquiry.dealBreakersMain,
        dealBreakerOther: this.inquiry.dealBreakerOther,
        idealJobDescription: this.inquiry.idealJobDescription,
      },
      jobsInquiryFormFields,
      locationDropdownOptions,
      companyStageDropdownOptions: companyStages,
      employmentTypeDropdownOptions: employmentTypes,
      isCustomLocationInputVisible: false,
      attemptedSubmission: false,
      loading: false,
      jobsCompanySeriesStages: undefined,
      isCustomPositionInputVisible: false,
      formFields,
    };
  },
  computed: {
    isSDR(): boolean {
      return this.form.positionMain === JobsInquiryPosition.Sdr;
    },
    dealbrakersDropdownOptions(): IDropdownOption[] {
      if (this.isSDR) return jobsDealBrakers.filter(o => o.key !== NO_SDR_KEY);
      return jobsDealBrakers;
    },
    isCustomLocation(): boolean {
      const currentCityId = Number(this.form.cityId);

      return !metroAreas.some(city => city.id === currentCityId);
    },
    positionMainDropdownOptions(): IDropdownOption[] {
      if (!this.form.positionMain) return salesRoleNames;

      return salesRoleNames.filter(option => option.key !== this.form.positionMain);
    },
    positionMainTogglerTitle(): string | null {
      if (!this.form.positionMain) return null;

      return jobsInquiryRoleNames[this.form.positionMain];
    },
    isPositionValid(): boolean {
      if (!this.attemptedSubmission) return true;

      return !this.$v?.form[jobsInquiryFormFields.POSITION_MAIN]?.$invalid;
    },
    positionError(): string | null {
      if (!this.attemptedSubmission) return null;

      return 'Please select position';
    },
    isCustomPositionValid(): boolean {
      if (!this.attemptedSubmission) return true;

      return !this.$v.form[jobsInquiryFormFields.POSITION_OTHER]?.$invalid;
    },
    customPositionError(): string | null {
      if (!this.attemptedSubmission || this.isCustomPositionValid) return null;

      return 'Please fill in position';
    },
    locationPredefinedTitle(): string | null {
      if (!this.form.cityId) return null;

      const location = this.locationDropdownOptions.find(location => location.key === Number(this.form.cityId));
      if (!location) return null;

      return location.title;
    },
    currentLocationCustom(): City | null {
      if (!this.form.cityId) return null;
      if (!this.isCustomLocation) return null;
      if (!this.profile?.city) return null;

      return this.profile.city;
    },
    isLocationValid(): boolean {
      if (!this.attemptedSubmission) return true;

      return !this.$v?.form?.cityId?.$invalid;
    },
    locationError(): string | null {
      if (!this.attemptedSubmission) return null;

      return 'Please select metro area';
    },
    companyStageTitle(): string | null {
      const companyStagesFormField = this.form.desiredCompanySeriesStages;

      if (!companyStagesFormField || !Array.isArray(companyStagesFormField)) return null;
      if (!companyStagesFormField.length) return null;

      return companyStagesFormField
        .map(companyStage => {
          const stage = companyStages.find(cs => cs.key === companyStage);

          return stage?.title;
        })
        .filter(Boolean)
        .join(', ');
    },
    isCompanyStagesValid(): boolean {
      if (!this.attemptedSubmission) return true;

      return !this.$v.form.desiredCompanySeriesStages?.$invalid;
    },
    companyStagesError(): string | null {
      if (!this.attemptedSubmission) return null;

      return 'Please select company stage';
    },
    dealbrakersTitle(): string | null {
      const dealBrakersFormField = this.form.dealBreakersMain;

      if (!dealBrakersFormField || !Array.isArray(dealBrakersFormField)) return null;
      if (!dealBrakersFormField.length) return null;

      if (dealBrakersFormField.length === 1) {
        const [dealBrakerKey] = dealBrakersFormField;
        const dealBraker = this.dealbrakersDropdownOptions.find(db => db.key === dealBrakerKey);

        return dealBraker?.title || null;
      }

      return `${dealBrakersFormField.length} deal breakers`;
    },
    isDealbrakersValid(): boolean {
      if (!this.attemptedSubmission) return true;

      return !this.$v.form[jobsInquiryFormFields.DEAL_BREAKERS_MAIN]?.$invalid;
    },
    dealbrakersError(): string | null {
      if (!this.attemptedSubmission) return null;

      return 'Please select deal breaker';
    },
    employmentTypeTitle(): string | null {
      const employmentTypesFormField = this.form.employmentTypes;

      if (!employmentTypesFormField || !Array.isArray(employmentTypesFormField)) return null;
      if (!employmentTypesFormField.length) return null;

      return (employmentTypesFormField as string[])
        ?.map(employmentType => {
          const type = this.employmentTypeDropdownOptions.find(et => et.key === employmentType);

          return type?.title;
        })
        .filter(Boolean)
        .join(', ');
    },
    isEmploymentTypeValid(): boolean {
      if (!this.attemptedSubmission) return true;

      return !this.$v.form[jobsInquiryFormFields.EMPLOYMENT_TYPES]?.$invalid;
    },
    employmentTypeError(): string | null {
      if (!this.attemptedSubmission) return null;

      return 'Please select employment types';
    },
  },
  watch: {
    isSDR(newValue): void {
      if (newValue) {
        const oldValue = this.form.dealBreakersMain ?? [];
        this.form.dealBreakersMain = oldValue.filter(o => o !== JobsInquiryDealBreaker.WithoutSdr);
      }
    },
  },
  mounted(): void {
    if (this.isCustomLocation) {
      this.isCustomLocationInputVisible = true;
    }

    if (this.inquiry.positionOther) {
      this.isCustomPositionInputVisible = true;
    }
  },
  methods: {
    handleDealbrakersInput(options: JobsInquiryDealBreaker[]): void {
      if (
        options.includes(JobsInquiryDealBreaker.None) &&
        this.form.dealBreakersMain &&
        !this.form.dealBreakersMain.includes(JobsInquiryDealBreaker.None)
      ) {
        this.form.dealBreakersMain = [JobsInquiryDealBreaker.None];
        this.form.dealBreakerOther = null;
        return;
      }

      this.form.dealBreakersMain = options.filter(option => option !== JobsInquiryDealBreaker.None);
    },
    handleDealbrakerOtherInput(): void {
      if (this.form.dealBreakersMain && this.form.dealBreakersMain.includes(JobsInquiryDealBreaker.None)) {
        this.form.dealBreakersMain = [];
      }
    },
    handleWillingToRelocate(): void {
      this.form.willingToRelocate = !this.form.willingToRelocate;
    },
    handleRoleOtherInput(): void {
      this.form.positionMain = null;
    },
    handleRoleMainInput(): void {
      this.isCustomPositionInputVisible = false;
      this.form.positionOther = null;

      this.form.positionOther = null;
    },
    handleLocationPredefinedInput(): void {
      this.isCustomLocationInputVisible = false;
    },
    showCustomLocationInput(): void {
      this.form.cityId = null;

      this.isCustomLocationInputVisible = true;

      this.$nextTick(() => {
        // @ts-ignore
        this.$refs.cityAutocomplete.focus();
      });
    },
    showCustomPositionInput(): void {
      this.form.positionMain = null;

      this.isCustomPositionInputVisible = true;

      this.$nextTick(() => {
        // @ts-ignore
        this.$refs.positionOtherInput.focus();
      });
    },
    handleSaveCustomLocation(cityId: string): void {
      this.form.cityId = cityId;
    },
    save(): void {
      this.attemptedSubmission = true;
      this.$v.form.$touch();

      if (this.$v.form.$invalid) return;

      const userAttrs = {
        cityId: Number(this.form.cityId),
      };

      const inquiryAttrs: InquiryInput = {
        ...omit(this.form, ['cityId', 'desiredCompanySeriesStages']),
        desiredCompanySeriesStageIds: this.getSelectedCompanyStages(),
      };

      this.$emit('upsert', {
        userAttrs,
        inquiryAttrs,
      });
    },
    getSelectedCompanyStages(): number[] {
      const selectedItems: number[] = (this.form.desiredCompanySeriesStages || [])?.reduce(
        (acc: number[], key: string) => {
          const companyStage = this.jobsCompanySeriesStages?.find(({ slug }) => slug === key);

          if (companyStage) {
            acc.push(parseInt(companyStage.id, 10));
          }

          return acc;
        },
        [],
      );

      return selectedItems;
    },
  },
});
